import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../../../components/ui/button';
import { PerformanceItem } from '../../../../lib/types';
import { formatCurrency, formatNumber } from '../../../../lib/export';
import { ArrowLeft } from 'lucide-react';

export function AllTopProducts() {
  const { brandId } = useParams<{ brandId: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<PerformanceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewType, setViewType] = useState<'revenue' | 'units'>('revenue');

  useEffect(() => {
    fetchAllProducts();
  }, []);

  const fetchAllProducts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/brand/${brandId}/dashboard/sales/top-products/all`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch all top products');
      }

      const data = await response.json();
      setData(data);
    } catch (error) {
      console.error('Error fetching all top products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/brand/${brandId}/dashboard/sales`);
  };

  const formatValue = (item: PerformanceItem) => {
    return viewType === 'revenue' ? formatCurrency(item.totalRevenue) : formatNumber(item.totalUnits);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={handleBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Sales Performance
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">All Top Performing Products</h1>
              <p className="text-gray-600">{data.length} products ranked by performance</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setViewType(viewType === 'revenue' ? 'units' : 'revenue')}
          >
            Toggle {viewType === 'revenue' ? 'Units' : 'Revenue'}
          </Button>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rank
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product Name
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total {viewType === 'revenue' ? 'Revenue' : 'Units'}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total {viewType === 'revenue' ? 'Units' : 'Revenue'}
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Performance
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.map((product, index) => (
                  <tr key={product.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-600">
                      #{index + 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {product.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                      {formatValue(product)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-600">
                      {viewType === 'revenue' ? formatNumber(product.totalUnits) : formatCurrency(product.totalRevenue)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        index < 3 ? 'bg-green-100 text-green-800' : 
                        index < 6 ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {index < 3 ? 'Excellent' : index < 6 ? 'Good' : 'Average'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
